import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { hotelService } from '@/services/hotel.service';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  CalendarDays, Users, Search, BedDouble, Coffee, ShieldCheck, Wallet, Loader2,
  Minus, Plus, CheckCircle2, ArrowRight, ImageIcon, Eye, ShoppingCart,
} from 'lucide-react';

const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const toISO = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fmtShort = (d) => (d ? d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }) : '');

function GuestStepper({ label, hint, value, min, max, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full"
          disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}
          data-testid={`${testid}-minus`} aria-label={`Diminuisci ${label}`}>
          <Minus className="h-4 w-4" />
        </Button>
        <span className="w-5 text-center text-sm font-semibold" data-testid={`${testid}-value`}>{value}</span>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full"
          disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))}
          data-testid={`${testid}-plus`} aria-label={`Aumenta ${label}`}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function RoomResultCard({ room, nights, onBook, onAddToCart, adding }) {
  const policies = [
    room.breakfast_included ? { icon: Coffee, label: 'Colazione inclusa' } : null,
    room.free_cancellation ? { icon: ShieldCheck, label: 'Cancellazione gratuita' } : null,
    room.pay_at_property ? { icon: Wallet, label: 'Paghi in struttura' } : null,
  ].filter(Boolean);
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card sm:flex-row" data-testid={`avail-room-${room.id}`}>
      <div className="relative h-40 w-full shrink-0 bg-muted sm:h-auto sm:w-44">
        {room.image ? (
          <img src={mediaUrl(room.image)} alt={room.name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-7 w-7 text-muted-foreground" /></div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{room.type}</Badge>
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Users className="h-3 w-3" />fino a {room.capacity_total}</span>
          {room.view && room.view !== 'Nessuna' && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Eye className="h-3 w-3" />Vista {room.view}</span>}
        </div>
        <h3 className="font-display text-lg font-semibold text-foreground">{room.name}</h3>
        {policies.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {policies.map((p, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-xs text-foreground/80"><p.icon className="h-3.5 w-3.5 text-primary" />{p.label}</span>
            ))}
          </div>
        )}
        <div className="mt-auto flex flex-wrap items-end justify-between gap-3 pt-2">
          <div>
            <span className="font-display text-xl font-bold text-foreground">{euro(room.total)}</span>
            <span className="text-sm text-muted-foreground"> · {nights} nott{nights === 1 ? 'e' : 'i'}</span>
            <p className="text-xs text-muted-foreground">{euro(room.per_night)} / notte</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onAddToCart(room)} disabled={adding} className="gap-1.5" data-testid={`avail-addcart-${room.id}`}>
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}Aggiungi
            </Button>
            <Button onClick={() => onBook(room)} data-testid={`avail-book-${room.id}`}>Prenota</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PublicBookingWidget({ ownerId, structureName }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();
  const { isCustomer: cartCustomer, addStay, openMiniCart } = useCart() || {};
  const isCustomer = user?.role === 'customer';

  const [range, setRange] = useState(undefined);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [dateOpen, setDateOpen] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {rooms, nights, count, stay_ok, min_stay}
  const [error, setError] = useState('');
  const [addingId, setAddingId] = useState(null);

  const [dialogRoom, setDialogRoom] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [booking, setBooking] = useState(false);
  const [success, setSuccess] = useState(null); // booking response

  const nights = useMemo(() => {
    if (range?.from && range?.to) return Math.max(0, Math.round((range.to - range.from) / 86400000));
    return 0;
  }, [range]);

  const canSearch = range?.from && range?.to && nights >= 1;

  const runSearch = async (ciISO, coISO, ad, ch) => {
    setLoading(true); setError(''); setResult(null);
    try {
      const data = await hotelService.publicAvailability({
        owner_id: ownerId, check_in: ciISO, check_out: coISO, adults: ad, children: ch,
      });
      setResult(data);
    } catch (e) {
      setError('Impossibile verificare la disponibilità. Riprova.');
    } finally { setLoading(false); }
  };

  const search = async () => {
    if (!canSearch) { toast.error('Seleziona le date di arrivo e partenza'); return; }
    await runSearch(toISO(range.from), toISO(range.to), adults, children);
  };

  // PRE-COMPILAZIONE da pre-ricerca Home: /struttura/:id?checkin=&checkout=&adults=&children=
  // Imposta date/ospiti e lancia automaticamente la ricerca disponibilità.
  useEffect(() => {
    const ci = sp.get('checkin');
    const co = sp.get('checkout');
    let ad = parseInt(sp.get('adults') || '', 10);
    let ch = parseInt(sp.get('children') || '', 10);
    if (!Number.isFinite(ad) || ad < 1) ad = adults;
    if (!Number.isFinite(ch) || ch < 0) ch = children;
    if (sp.get('adults')) setAdults(ad);
    if (sp.get('children')) setChildren(ch);
    if (ci && co) {
      const from = new Date(`${ci}T00:00:00`);
      const to = new Date(`${co}T00:00:00`);
      if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime()) && to > from) {
        setRange({ from, to });
        runSearch(ci, co, ad, ch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openBooking = (room) => {
    setDialogRoom(room);
    setSuccess(null);
    setForm({
      name: isCustomer ? (user?.name || '') : '',
      email: isCustomer ? (user?.email || '') : '',
      phone: isCustomer ? (user?.phone || '') : '',
      notes: '',
    });
  };

  const addStayToCart = async (room) => {
    if (!isCustomer) {
      toast.info('Accedi per usare il carrello');
      navigate(`/cliente/accedi?next=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    setAddingId(room.id);
    try {
      await addStay({
        owner_id: ownerId, room_id: room.id,
        check_in: toISO(range.from), check_out: toISO(range.to),
        adults, children,
      });
      toast.success('Camera aggiunta al carrello — trattenuta per te per 15 minuti');
      openMiniCart?.();
      // rimuovi dai risultati: ora è trattenuta per l'utente (evita doppioni)
      setResult((prev) => prev ? { ...prev, rooms: prev.rooms.filter((r) => r.id !== room.id), count: prev.count - 1 } : prev);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Impossibile aggiungere al carrello. Riprova.');
    } finally { setAddingId(null); }
  };

  const confirmBooking = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error('Nome ed email sono obbligatori'); return; }
    setBooking(true);
    try {
      const data = await hotelService.publicBook({
        owner_id: ownerId, room_id: dialogRoom.id,
        check_in: toISO(range.from), check_out: toISO(range.to),
        guest: {
          name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
          adults, children, notes: form.notes.trim(),
        },
      });
      setSuccess(data);
      // rimuovi la camera prenotata dai risultati (non più disponibile per queste date)
      setResult((prev) => prev ? { ...prev, rooms: prev.rooms.filter((r) => r.id !== dialogRoom.id), count: prev.count - 1 } : prev);
      toast.success('Richiesta di prenotazione inviata!');
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Prenotazione non riuscita. Riprova.');
    } finally { setBooking(false); }
  };

  const closeDialog = () => { setDialogRoom(null); setSuccess(null); };

  return (
    <section className="mt-4" data-testid="public-booking-widget">
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
        <h2 className="flex items-center gap-2 font-display text-xl font-bold tracking-tight text-foreground">
          <CalendarDays className="h-5 w-5 text-primary" />Verifica disponibilità
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">Scegli le date e gli ospiti: la disponibilità è aggiornata in tempo reale.</p>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          {/* DATE RANGE */}
          <div>
            <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">Date del soggiorno</Label>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start gap-2 font-normal" data-testid="booking-dates-trigger">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  {range?.from && range?.to
                    ? <span>{fmtShort(range.from)} → {fmtShort(range.to)} · {nights} nott{nights === 1 ? 'e' : 'i'}</span>
                    : <span className="text-muted-foreground">Arrivo — Partenza</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={range}
                  onSelect={setRange}
                  numberOfMonths={1}
                  disabled={{ before: new Date() }}
                  data-testid="booking-calendar"
                />
                <div className="flex justify-end border-t border-border p-2">
                  <Button size="sm" onClick={() => setDateOpen(false)} data-testid="booking-dates-done">Fatto</Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* GUESTS */}
          <div>
            <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">Ospiti</Label>
            <Popover open={guestsOpen} onOpenChange={setGuestsOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start gap-2 font-normal sm:w-44" data-testid="booking-guests-trigger">
                  <Users className="h-4 w-4 text-primary" />{adults + children} ospit{adults + children === 1 ? 'e' : 'i'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64" align="start">
                <GuestStepper label="Adulti" value={adults} min={1} max={12} onChange={setAdults} testid="booking-adults" />
                <GuestStepper label="Bambini" hint="0-12 anni" value={children} min={0} max={10} onChange={setChildren} testid="booking-children" />
              </PopoverContent>
            </Popover>
          </div>

          <Button onClick={search} disabled={loading} className="gap-2 sm:w-auto" data-testid="booking-search-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Cerca
          </Button>
        </div>

        {/* RESULTS */}
        <div className="mt-4">
          {loading && (
            <div className="space-y-3" data-testid="avail-loading">
              {[0, 1].map((i) => <Skeleton key={i} className="h-32 w-full rounded-2xl" />)}
            </div>
          )}
          {!loading && error && (
            <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="avail-error">{error}</p>
          )}
          {!loading && result && result.bookings_open === false && (
            <p className="rounded-xl bg-accent px-4 py-3 text-sm text-accent-foreground" data-testid="avail-closed">
              Le prenotazioni online non sono disponibili al momento. Contatta direttamente la struttura.
            </p>
          )}
          {!loading && result && result.bookings_open !== false && !result.stay_ok && (
            <p className="rounded-xl bg-accent px-4 py-3 text-sm text-accent-foreground" data-testid="avail-stay-note">
              Questa struttura richiede un soggiorno di almeno {result.min_stay} nott{result.min_stay === 1 ? 'e' : 'i'}
              {result.max_stay ? ` e massimo ${result.max_stay}` : ''}.
            </p>
          )}
          {!loading && result && result.bookings_open !== false && result.stay_ok && result.count === 0 && (
            <div className="rounded-xl border border-dashed border-border bg-muted/40 px-4 py-8 text-center text-sm text-muted-foreground" data-testid="avail-empty">
              Nessuna camera disponibile per le date selezionate. Prova a cambiare le date.
            </div>
          )}
          {!loading && result && result.bookings_open !== false && result.stay_ok && result.count > 0 && (
            <div className="space-y-3" data-testid="avail-results">
              <p className="text-sm font-medium text-foreground">{result.count} camer{result.count === 1 ? 'a disponibile' : 'e disponibili'} per {result.nights} nott{result.nights === 1 ? 'e' : 'i'}</p>
              {result.rooms.map((r) => <RoomResultCard key={r.id} room={r} nights={result.nights} onBook={openBooking} onAddToCart={addStayToCart} adding={addingId === r.id} />)}
            </div>
          )}
        </div>
      </div>

      {/* BOOKING DIALOG */}
      <Dialog open={!!dialogRoom} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent data-testid="booking-dialog">
          {dialogRoom && !success && (
            <form onSubmit={confirmBooking}>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Completa la prenotazione</DialogTitle>
                <DialogDescription>
                  {dialogRoom.name} · {fmtShort(range?.from)} → {fmtShort(range?.to)} · {euro(dialogRoom.total)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {isCustomer && (
                  <p className="rounded-lg bg-primary/10 px-3 py-2 text-xs text-primary" data-testid="booking-logged-note">
                    Prenoti come <b>{user?.email}</b> — la troverai nell'Area Clienti.
                  </p>
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5"><Label htmlFor="bk-name">Nome e cognome</Label><Input id="bk-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="bk-phone">Telefono</Label><Input id="bk-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 ..." data-testid="booking-phone-input" /></div>
                </div>
                <div className="space-y-1.5"><Label htmlFor="bk-email">Email</Label><Input id="bk-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="bk-notes">Note (opzionale)</Label><Input id="bk-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Richieste particolari, orario di arrivo..." data-testid="booking-notes-input" /></div>
                <p className="text-xs text-muted-foreground">La struttura confermerà la disponibilità. Nessun pagamento richiesto ora.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={closeDialog} data-testid="booking-cancel">Annulla</Button>
                <Button type="submit" disabled={booking} data-testid="booking-confirm">{booking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Invia richiesta</Button>
              </DialogFooter>
            </form>
          )}
          {success && (
            <div className="py-2 text-center" data-testid="booking-success">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"><CheckCircle2 className="h-8 w-8" /></div>
              <DialogHeader className="mt-3">
                <DialogTitle className="text-center font-display text-xl">Richiesta inviata!</DialogTitle>
                <DialogDescription className="text-center">
                  {structureName || success.structure_name} confermerà a breve. Codice: <b className="font-mono text-foreground">{success.code}</b>
                </DialogDescription>
              </DialogHeader>
              <p className="mt-3 text-sm text-muted-foreground">
                {success.room_name} · {success.nights} nott{success.nights === 1 ? 'e' : 'i'} · {euro(success.total)} — stato: <b>In attesa di conferma</b>
              </p>
              <DialogFooter className="mt-4 sm:justify-center">
                {success.linked_to_account ? (
                  <Button onClick={() => navigate('/cliente/prenotazioni')} className="gap-1" data-testid="booking-success-area">
                    Vedi le mie prenotazioni <ArrowRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button variant="outline" onClick={closeDialog} data-testid="booking-success-close">Chiudi</Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default PublicBookingWidget;
